import React from 'react'
import posthog from 'posthog-js'

class ReactErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null, isChunkLoadError: false }
  }

  static getDerivedStateFromError(error) {
    const isChunkLoadError =
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('Importing a module script failed') ||
      error.message?.includes('ChunkLoadError') ||
      error.name === 'ChunkLoadError'

    return { hasError: true, isChunkLoadError }
  }

  componentDidCatch(error, errorInfo) {
    const isChunkLoadError = this.state.isChunkLoadError
    const isLocalhost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname.includes('.localhost'))

    // Skip error tracking on localhost
    if (typeof posthog !== 'undefined' && posthog.capture && !isLocalhost) {
      // Generate trace ID for error correlation
      const sessionId =
        posthog?.get_session_id?.() ||
        `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
      const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
      const traceId = `${sessionId}.${requestId}`

      posthog.capture('$exception', {
        $exception_type: error.name,
        $exception_message: error.message,
        $exception_stack: error.stack,
        $exception_source: 'error_boundary',
        trace_id: traceId,
        componentStack: errorInfo.componentStack,
        isValidationError: this.isValidationError(error),
        isChunkLoadError,
      })
    }

    this.setState({
      error,
      errorInfo,
    })

    console.error('Error caught by boundary:', error, errorInfo)

    if (isChunkLoadError) {
      console.warn(
        'Chunk loading error detected - likely a new deployment. Auto-reloading in 3 seconds...'
      )
      setTimeout(() => {
        window.location.reload()
      }, 3000)
    }
  }

  isValidationError(error) {
    if (!error) {
      return false
    }

    if (error.name === 'GQtyError' || error.constructor?.name === 'GQtyError') {
      return true
    }

    if (
      error.extensions?.code === 'DEFAULT_LOCATION_EXISTS' ||
      error.extensions?.code === 'VALIDATION_ERROR'
    ) {
      return true
    }

    const message = error.message || error.toString()
    const validationMessages = [
      'Only one location can be set as default',
      'Cannot delete the only location',
      'Cannot unset default location',
      'Time must be in HH:MM format',
      'ValidationError',
      'Please unset the current default location first',
    ]

    return validationMessages.some((msg) => message.includes(msg))
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.isValidationError(this.state.error)) {
        this.setState({ hasError: false, error: null, errorInfo: null, isChunkLoadError: false })
        return this.props.children
      }

      if (this.props.fallback) {
        return React.cloneElement(this.props.fallback, {
          error: this.state.error,
          errorInfo: this.state.errorInfo,
          isChunkLoadError: this.state.isChunkLoadError,
          retry: this.handleRetry,
        })
      }

      return this.props.children
    }

    return this.props.children
  }
}

export default ReactErrorBoundary
